import { ConfirmDialog } from '@/components/confirm-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { listAttendantsQuery } from '@/queries/attendant.query'
import { listDepartmentsQuery } from '@/queries/department.query'
import {
  listAgentTicketsQuery,
  listDeskTicketsQuery,
  listFinishedTicketsQuery,
  listPendingTicketsQuery,
} from '@/queries/ticket.query'
import { TicketService } from '@/services/ticket.service'
import {
  selectClearTicket,
  selectCurrentTicketId,
  selectDialog,
  selectSetDialog,
  useChatStore,
  type ChatsDialogType,
} from '@/stores/chat.store'
import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { Info } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { getCurrentChatTicket } from '../utils/ticket-routing'
import { ChatContactEditSheet } from './chat-contact-edit-sheet'
import { NewChatDialog } from './new-chat'

export function ChatsDialogs() {
  const open = useChatStore(selectDialog)
  const setOpen = useChatStore(selectSetDialog)
  const currentTicketId = useChatStore(selectCurrentTicketId)
  const clearTicket = useChatStore(selectClearTicket)
  const queryClient = useQueryClient()
  const { data: agentTickets } = useSuspenseQuery(listAgentTicketsQuery)
  const { data: deskTickets } = useSuspenseQuery(listDeskTicketsQuery)
  const { data: pendingTickets } = useSuspenseQuery(listPendingTicketsQuery)
  const { data: finishedTickets } = useSuspenseQuery(listFinishedTicketsQuery)
  const { data: departments } = useQuery(listDepartmentsQuery)
  const { data: attendants } = useQuery(listAttendantsQuery)
  const [selectedAttendant, setSelectedAttendant] = useState<string>('')
  const [selectedDepartment, setSelectedDepartment] = useState<string>('')
  const [transferType, setTransferType] = useState<
    'DEPARTMENT' | 'ATTENDANT' | null
  >(null)

  const currentTicket = getCurrentChatTicket(
    currentTicketId,
    agentTickets,
    deskTickets,
    pendingTickets,
    finishedTickets
  )

  const availableDepartments = useMemo(() => {
    if (!departments || !currentTicket?.department) return departments || []
    return departments.filter((dept) => dept.id !== currentTicket.department.id)
  }, [departments, currentTicket?.department])

  const hasOtherDepartments = availableDepartments.length > 0

  const availableAttendants = useMemo(() => {
    const attendantsList = attendants?.attendants || []

    if (!currentTicket?.attendantId) {
      return attendantsList
    }

    return attendantsList.filter(
      (attendant) => attendant.userId !== currentTicket.attendantId
    )
  }, [attendants?.attendants, currentTicket?.attendantId])

  const hasOtherAttendants = availableAttendants.length > 0

  const resetTransferForm = () => {
    setSelectedAttendant('')
    setSelectedDepartment('')
    setTransferType(null)
  }

  const handleTransferTypeChange = (value: string) => {
    if (value === 'DEPARTMENT' || value === 'ATTENDANT') {
      setSelectedAttendant('')
      setSelectedDepartment('')
      setTransferType(value)
      return
    }

    setTransferType(null)
  }

  const handleTransfer = () => {
    if (!currentTicket || !transferType) return

    let transferPromise: Promise<unknown> | null = null
    if (transferType === 'ATTENDANT') {
      if (!selectedAttendant) return
      transferPromise = TicketService.transferToAttendant(
        currentTicket.id,
        selectedAttendant
      )
    }
    if (transferType === 'DEPARTMENT') {
      if (!selectedDepartment) return
      transferPromise = TicketService.transferToDepartment(
        currentTicket.id,
        Number(selectedDepartment)
      )
    }
    if (!transferPromise) return
    setOpen(null)

    toast.promise(transferPromise, {
      loading: 'Transferindo atendimento...',
      success: () => {
        clearTicket()
        resetTransferForm()
        return 'Atendimento transferido com sucesso'
      },
      error: 'Erro ao transferir atendimento',
      finally: () => {
        queryClient.invalidateQueries(listAgentTicketsQuery)
        queryClient.invalidateQueries(listDeskTicketsQuery)
        queryClient.invalidateQueries(listPendingTicketsQuery)
        queryClient.invalidateQueries(listFinishedTicketsQuery)
      },
    })
  }

  const handleFinalize = () => {
    if (!currentTicket) return

    setOpen(null)
    toast.promise(TicketService.close(currentTicket.id), {
      loading: 'Finalizando atendimento...',
      success: 'Atendimento finalizado',
      error: 'Erro ao finalizar atendimento!',
      finally: async () => {
        queryClient.invalidateQueries(listAgentTicketsQuery)
        queryClient.invalidateQueries(listDeskTicketsQuery)
        queryClient.invalidateQueries(listFinishedTicketsQuery)
        queryClient.invalidateQueries(listPendingTicketsQuery)
        clearTicket()
      },
    })
  }

  const handleDialogChange =
    (dialog: ChatsDialogType) => (nextOpen: boolean) => {
      if (!nextOpen) {
        if (dialog === 'transfer') {
          resetTransferForm()
        }
        setOpen(null)
        return
      }

      setOpen(dialog)
    }

  return (
    <>
      <NewChatDialog
        key='new-chat'
        open={open === 'new'}
        onOpenChange={(nextOpen) => setOpen(nextOpen ? 'new' : null)}
      />

      <ConfirmDialog
        key='ticket-finalize'
        destructive
        open={open === 'finalize'}
        onOpenChange={handleDialogChange('finalize')}
        handleConfirm={handleFinalize}
        className='max-w-md'
        title='Finalizar este atendimento?'
        desc={
          <>
            Você está prestes a finalizar um atendimento de{' '}
            <strong>{currentTicket?.contact.name}</strong>. <br />
            Esta ação não pode ser revertida.
          </>
        }
        confirmText='Finalizar'
      />

      <ChatContactEditSheet
        key='edit-contact'
        open={open === 'editContact'}
        onOpenChange={handleDialogChange('editContact')}
        contact={currentTicket?.contact}
      />

      <Dialog
        open={open === 'transfer'}
        onOpenChange={handleDialogChange('transfer')}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transferir Atendimento</DialogTitle>
            <DialogDescription>
              Selecione o tipo de transferência e depois escolha o destino para
              o atendimento de <strong>{currentTicket?.contact.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='transfer-type'>Tipo de Transferência</Label>
              <RadioGroup
                id='transfer-type'
                value={transferType ?? ''}
                onValueChange={handleTransferTypeChange}
                aria-label='Tipo de Transferência'
              >
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value='DEPARTMENT' id='transfer-department' />
                  <Label htmlFor='transfer-department'>
                    Transferir para Departamento
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value='ATTENDANT' id='transfer-attendant' />
                  <Label htmlFor='transfer-attendant'>
                    Transferir para Atendente
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {transferType === 'DEPARTMENT' && (
              <div className='space-y-2'>
                <Label htmlFor='department'>Departamento</Label>
                <Select
                  disabled={!hasOtherDepartments}
                  value={selectedDepartment}
                  onValueChange={setSelectedDepartment}
                >
                  <SelectTrigger
                    id='department'
                    aria-disabled={!hasOtherDepartments}
                  >
                    <SelectValue
                      placeholder={
                        hasOtherDepartments
                          ? 'Selecione um departamento'
                          : 'Nenhum departamento disponível'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className='z-[60]'>
                    {availableDepartments.map((dept) => (
                      <SelectItem key={dept.id} value={String(dept.id)}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!hasOtherDepartments && (
                  <Alert>
                    <Info className='h-4 w-4' />
                    <AlertTitle>Sem departamentos disponíveis</AlertTitle>
                    <AlertDescription>
                      Não há outros departamentos disponíveis para a
                      transferência
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {transferType === 'ATTENDANT' && (
              <div className='space-y-2'>
                <Label htmlFor='attendant'>Atendente</Label>
                <Select
                  disabled={!hasOtherAttendants}
                  value={selectedAttendant}
                  onValueChange={setSelectedAttendant}
                >
                  <SelectTrigger
                    id='attendant'
                    aria-disabled={!hasOtherAttendants}
                  >
                    <SelectValue
                      placeholder={
                        hasOtherAttendants
                          ? 'Selecione um atendente'
                          : 'Nenhum atendente disponível'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className='z-[60]'>
                    {availableAttendants.map((att) => (
                      <SelectItem key={att.id} value={att.userId}>
                        {att.user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!hasOtherAttendants && (
                  <Alert>
                    <Info className='h-4 w-4' />
                    <AlertTitle>Sem atendentes disponíveis</AlertTitle>
                    <AlertDescription>
                      Não há outros atendentes disponíveis para a transferência
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setOpen(null)
                resetTransferForm()
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleTransfer}
              disabled={
                !transferType ||
                (transferType === 'ATTENDANT' && !selectedAttendant) ||
                (transferType === 'DEPARTMENT' && !selectedDepartment)
              }
            >
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
